import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Users,
  Calendar,
  DollarSign,
  Activity,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Clock,
  Heart,
  Eye,
  Brain,
  Stethoscope,
  Pill,
  Syringe,
  Building2,
  UserCog,
  FileText,
  BarChart3,
  PieChart,
  Bell,
  Settings,
  ChevronRight,
  RefreshCw,
  Filter,
  Plus,
  Droplets,
  Thermometer,
  Scan,
  Smile,
  Shield,
  AlertCircle,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { format, isToday, isTomorrow } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { dashboardApi, appointmentsApi, getLocalizedValue } from '@/services/clinicApi';

// Specialty configuration with icons, colors, and labels
const SPECIALTY_CONFIG = {
  cardiology: {
    icon: Heart,
    color: 'text-red-500',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    borderColor: 'border-red-500',
    label: { en: 'Cardiology', ar: 'طب القلب' },
    description: { en: 'Heart & Cardiovascular', ar: 'القلب والأوعية الدموية' },
  },
  ophthalmology: {
    icon: Eye,
    color: 'text-blue-500',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    borderColor: 'border-blue-500',
    label: { en: 'Ophthalmology', ar: 'طب العيون' },
    description: { en: 'Eye Care & Vision', ar: 'العناية بالعيون والرؤية' },
  },
  neurology: {
    icon: Brain,
    color: 'text-purple-500',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    borderColor: 'border-purple-500',
    label: { en: 'Neurology', ar: 'طب الأعصاب' },
    description: { en: 'Brain & Nervous System', ar: 'الدماغ والجهاز العصبي' },
  },
  dental: {
    icon: Smile,
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-100 dark:bg-cyan-900/30',
    borderColor: 'border-cyan-500',
    label: { en: 'Dental', ar: 'طب الأسنان' },
    description: { en: 'Oral Health & Dentistry', ar: 'صحة الفم وطب الأسنان' },
  },
  hematology: {
    icon: Droplets,
    color: 'text-rose-500',
    bgColor: 'bg-rose-100 dark:bg-rose-900/30',
    borderColor: 'border-rose-500',
    label: { en: 'Hematology', ar: 'أمراض الدم' },
    description: { en: 'Blood Disorders', ar: 'اضطرابات الدم' },
  },
  oncology: {
    icon: Shield,
    color: 'text-orange-500',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    borderColor: 'border-orange-500',
    label: { en: 'Oncology', ar: 'الأورام' },
    description: { en: 'Cancer Care', ar: 'علاج السرطان' },
  },
  allergy: {
    icon: Syringe,
    color: 'text-green-500',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    borderColor: 'border-green-500',
    label: { en: 'Allergy & Immunology', ar: 'الحساسية والمناعة' },
    description: { en: 'Allergies & Immune System', ar: 'الحساسية والجهاز المناعي' },
  },
  general: {
    icon: Stethoscope,
    color: 'text-teal-500',
    bgColor: 'bg-teal-100 dark:bg-teal-900/30',
    borderColor: 'border-teal-500',
    label: { en: 'General Medicine', ar: 'الطب العام' },
    description: { en: 'Primary Care', ar: 'الرعاية الأولية' },
  },
};

// KPI Icon mapping based on icon name from API
const getKPIIcon = (iconName, specialty) => {
  const iconMap = {
    'fa-users': Users,
    'fa-user': Users,
    'fa-heartbeat': Heart,
    'fa-heart': Heart,
    'fa-calendar': Calendar,
    'fa-calendar-check': Calendar,
    'fa-calendar-week': Calendar,
    'fa-clock': Clock,
    'fa-clipboard-list': FileText,
    'fa-tooth': Smile,
    'fa-tools': Settings,
    'fa-eye': Eye,
    'fa-brain': Brain,
    'fa-tint': Droplets,
    'fa-pills': Pill,
    'fa-syringe': Syringe,
    'fa-shield': Shield,
  };
  
  if (iconName && iconMap[iconName]) {
    return iconMap[iconName];
  }
  
  // Fallback to specialty icon
  return SPECIALTY_CONFIG[specialty]?.icon || Activity;
};

// KPI Card colors based on color name
const getKPIColorClasses = (colorName) => {
  const colorMap = {
    primary: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' },
    success: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400' },
    danger: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400' },
    warning: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400' },
    info: { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-600 dark:text-teal-400' },
  };
  return colorMap[colorName] || colorMap.info;
};

// Specialty-specific chart component
const SpecialtyChart = ({ chartData, specialty, lang }) => {
  if (!chartData) return null;
  
  const config = SPECIALTY_CONFIG[specialty] || SPECIALTY_CONFIG.general;
  
  // Render line chart for trends (like BP trends for cardiology)
  const renderLineChart = (chart) => {
    if (!chart.datasets || chart.datasets.length === 0) return null;
    
    const maxValue = Math.max(
      ...chart.datasets.flatMap(ds => ds.data || [])
    ) || 100;
    
    return (
      <Card className="border-0 shadow-md">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className={`h-5 w-5 ${config.color}`} />
            {chart.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-48 relative">
            {/* Y-axis labels */}
            <div className="absolute left-0 top-0 bottom-8 w-10 flex flex-col justify-between text-xs text-slate-400">
              <span>{maxValue}</span>
              <span>{Math.round(maxValue / 2)}</span>
              <span>0</span>
            </div>
            {/* Chart area */}
            <div className="ml-12 h-40 flex items-end gap-1">
              {chart.labels?.map((label, idx) => (
                <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                  {chart.datasets.map((dataset, dsIdx) => {
                    const value = dataset.data?.[idx] || 0;
                    const height = (value / maxValue) * 100;
                    return (
                      <div
                        key={dsIdx}
                        className="w-full rounded-t transition-all"
                        style={{
                          height: `${height}%`,
                          backgroundColor: dataset.borderColor || (dsIdx === 0 ? '#ef4444' : '#3b82f6'),
                          opacity: 0.8,
                        }}
                        title={`${dataset.label}: ${value}`}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
            {/* X-axis labels */}
            <div className="ml-12 flex justify-between mt-2 text-xs text-slate-400">
              {chart.labels?.slice(0, 6).map((label, idx) => (
                <span key={idx} className="truncate">{label}</span>
              ))}
            </div>
          </div>
          {/* Legend */}
          <div className="flex items-center justify-center gap-4 mt-4">
            {chart.datasets?.map((dataset, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded" 
                  style={{ backgroundColor: dataset.borderColor }}
                />
                <span className="text-sm text-slate-600 dark:text-slate-300">{dataset.label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };
  
  // Render doughnut/pie chart for status distribution
  const renderDoughnutChart = (chart) => {
    if (!chart.data || chart.data.length === 0) return null;
    
    const total = chart.data.reduce((sum, val) => sum + val, 0);
    const colors = ['#14b8a6', '#3b82f6', '#22c55e', '#ef4444', '#f59e0b', '#8b5cf6'];
    
    return (
      <Card className="border-0 shadow-md">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <PieChart className={`h-5 w-5 ${config.color}`} />
            {chart.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {chart.labels?.map((label, idx) => {
              const value = chart.data[idx] || 0;
              const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
              return (
                <div key={idx} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-300">{label}</span>
                    <span className="font-medium">{value} ({percentage}%)</span>
                  </div>
                  <Progress 
                    value={percentage} 
                    className="h-2"
                    style={{ '--progress-color': colors[idx % colors.length] }}
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };
  
  // Render all charts
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {Object.entries(chartData).map(([key, chart]) => {
        if (chart.type === 'line') {
          return <div key={key}>{renderLineChart(chart)}</div>;
        }
        if (chart.type === 'doughnut' || chart.type === 'pie') {
          return <div key={key}>{renderDoughnutChart(chart)}</div>;
        }
        return null;
      })}
    </div>
  );
};

// Specialty Header Component
const SpecialtyHeader = ({ specialty, lang }) => {
  const config = SPECIALTY_CONFIG[specialty] || SPECIALTY_CONFIG.general;
  const Icon = config.icon;
  
  return (
    <div className={`flex items-center gap-3 p-4 rounded-lg ${config.bgColor} border-l-4 ${config.borderColor}`}>
      <div className={`p-3 rounded-full bg-white dark:bg-slate-800`}>
        <Icon className={`h-8 w-8 ${config.color}`} />
      </div>
      <div>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white">
          {config.label[lang] || config.label.en}
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {config.description[lang] || config.description.en}
        </p>
      </div>
    </div>
  );
};

const ClinicDashboard = () => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');
  const [availableSpecialties, setAvailableSpecialties] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [recentRecords, setRecentRecords] = useState([]);
  const [doctorInfo, setDoctorInfo] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchDashboardData();
  }, [isAuthenticated, navigate]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [summaryRes, specialtiesRes, appointmentsRes] = await Promise.all([
        dashboardApi.getSummary().catch(() => null),
        dashboardApi.getAvailableSpecialties().catch(() => null),
        appointmentsApi.getAll({
          with: 'appointmentStatus;patient',
          limit: 10,
          orderBy: 'appointment_at',
          sortedBy: 'asc'
        }).catch(() => null),
      ]);

      if (summaryRes?.data?.data) {
        setDashboardData(summaryRes.data.data);
        setAlerts(summaryRes.data.data.alerts || []);
        setRecentRecords(summaryRes.data.data.recent_records || []);
        
        // Set detected specialty from API response
        if (summaryRes.data.data.specialty) {
          setSelectedSpecialty(summaryRes.data.data.specialty);
        }
      }
      
      if (summaryRes?.data?.doctor) {
        setDoctorInfo(summaryRes.data.doctor);
      }

      if (specialtiesRes?.data?.data) {
        setAvailableSpecialties(Object.entries(specialtiesRes.data.data).map(([key, value]) => ({
          id: key,
          name: value,
        })));
      }

      if (appointmentsRes?.data?.data) {
        setUpcomingAppointments(appointmentsRes.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error(lang === 'ar' ? 'فشل في تحميل لوحة التحكم' : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSpecialtyChange = async (specialty) => {
    setSelectedSpecialty(specialty);
    setLoading(true);
    
    try {
      if (specialty !== 'all') {
        const response = await dashboardApi.getBySpecialty(specialty);
        if (response?.data?.data) {
          setDashboardData(response.data.data);
          setAlerts(response.data.data.alerts || []);
          setRecentRecords(response.data.data.recent_records || []);
        }
      } else {
        await fetchDashboardData();
      }
    } catch (error) {
      console.error('Failed to fetch specialty dashboard:', error);
      toast.error(lang === 'ar' ? 'فشل في تحميل بيانات التخصص' : 'Failed to load specialty data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (statusId) => {
    const statusMap = {
      1: { label: lang === 'ar' ? 'معلق' : 'Pending', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
      2: { label: lang === 'ar' ? 'مؤكد' : 'Confirmed', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
      3: { label: lang === 'ar' ? 'في الطريق' : 'On the Way', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
      4: { label: lang === 'ar' ? 'جاري' : 'In Progress', color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' },
      5: { label: lang === 'ar' ? 'مكتمل' : 'Completed', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
      6: { label: lang === 'ar' ? 'ملغي' : 'Cancelled', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    };
    const status = statusMap[statusId] || statusMap[1];
    return <Badge className={status.color}>{status.label}</Badge>;
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'danger': return <XCircle className="h-5 w-5 text-red-500" />;
      case 'warning': return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      case 'success': return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      default: return <AlertCircle className="h-5 w-5 text-blue-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-24" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Skeleton className="h-96 lg:col-span-2" />
            <Skeleton className="h-96" />
          </div>
        </div>
      </div>
    );
  }

  const currentSpecialty = dashboardData?.specialty || selectedSpecialty || 'general';
  const specialtyConfig = SPECIALTY_CONFIG[currentSpecialty] || SPECIALTY_CONFIG.general;
  
  // Get KPI cards from API or use defaults
  const kpiCards = dashboardData?.cards || [
    { title: lang === 'ar' ? 'إجمالي المرضى' : 'Total Patients', value: dashboardData?.totalPatients || 0, icon: 'fa-users', color: 'primary' },
    { title: lang === 'ar' ? 'مواعيد اليوم' : "Today's Appointments", value: dashboardData?.todayAppointments || 0, icon: 'fa-calendar', color: 'success' },
    { title: lang === 'ar' ? 'مواعيد معلقة' : 'Pending Appointments', value: dashboardData?.pendingAppointments || 0, icon: 'fa-clock', color: 'warning' },
    { title: lang === 'ar' ? 'مواعيد الأسبوع' : 'This Week', value: dashboardData?.weekAppointments || 0, icon: 'fa-calendar-week', color: 'info' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="clinic-dashboard">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                {lang === 'ar' ? 'لوحة تحكم العيادة' : 'Clinic Dashboard'}
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                {lang === 'ar' ? 'مرحباً،' : 'Welcome,'} {doctorInfo?.name || getLocalizedValue(user?.name, lang) || user?.name}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Specialty Filter */}
              <Select value={selectedSpecialty} onValueChange={handleSpecialtyChange}>
                <SelectTrigger className="w-56" data-testid="specialty-filter">
                  <SelectValue placeholder={lang === 'ar' ? 'اختر التخصص' : 'Select Specialty'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{lang === 'ar' ? 'الكل' : 'All Specialties'}</SelectItem>
                  {availableSpecialties.map((spec) => (
                    <SelectItem key={spec.id} value={spec.id}>
                      {spec.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={fetchDashboardData} data-testid="refresh-btn">
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" asChild>
                <Link to="/clinic/settings">
                  <Settings className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Specialty Header - Only show when specific specialty is selected */}
        {selectedSpecialty !== 'all' && (
          <SpecialtyHeader specialty={currentSpecialty} lang={lang} />
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
          {kpiCards.map((card, index) => {
            const IconComponent = getKPIIcon(card.icon, currentSpecialty);
            const colorClasses = getKPIColorClasses(card.color);
            
            return (
              <Card key={index} className="border-0 shadow-md hover:shadow-lg transition-shadow" data-testid={`kpi-card-${index}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                        {card.title}
                      </p>
                      <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">
                        {card.value?.toLocaleString() || 0}
                      </p>
                      {card.trend !== null && card.trend !== undefined && (
                        <div className={`flex items-center gap-1 text-sm mt-2 ${card.trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {card.trend > 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                          <span>{Math.abs(card.trend)}%</span>
                        </div>
                      )}
                    </div>
                    <div className={`p-3 rounded-xl ${colorClasses.bg}`}>
                      <IconComponent className={`h-6 w-6 ${colorClasses.text}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Specialty-specific Charts */}
        {dashboardData?.charts && (
          <div data-testid="specialty-charts">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <BarChart3 className={`h-5 w-5 ${specialtyConfig.color}`} />
              {lang === 'ar' ? 'التحليلات والرسوم البيانية' : 'Analytics & Charts'}
            </h3>
            <SpecialtyChart 
              chartData={dashboardData.charts} 
              specialty={currentSpecialty} 
              lang={lang} 
            />
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Appointments Section */}
          <Card className="lg:col-span-2 border-0 shadow-md" data-testid="appointments-section">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{lang === 'ar' ? 'المواعيد القادمة' : 'Upcoming Appointments'}</CardTitle>
                <CardDescription>
                  {lang === 'ar' ? 'الأيام السبعة القادمة' : 'Next 7 days'}
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to="/doctor/appointments">
                  {lang === 'ar' ? 'عرض الكل' : 'View All'}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                {upcomingAppointments.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                    <Calendar className="h-12 w-12 mb-4 opacity-50" />
                    <p>{lang === 'ar' ? 'لا توجد مواعيد قادمة' : 'No upcoming appointments'}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {upcomingAppointments.map((apt) => {
                      const aptDate = new Date(apt.appointment_at);
                      const patientName = apt.patient 
                        ? `${getLocalizedValue(apt.patient.first_name, lang) || ''} ${getLocalizedValue(apt.patient.last_name, lang) || ''}`.trim()
                        : (lang === 'ar' ? 'مريض' : 'Patient');
                      
                      return (
                        <div 
                          key={apt.id} 
                          className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                          onClick={() => navigate(`/doctor/appointments/${apt.id}`)}
                          data-testid={`appointment-${apt.id}`}
                        >
                          <Avatar>
                            <AvatarImage src={apt.patient?.media?.[0]?.url} />
                            <AvatarFallback className={`${specialtyConfig.bgColor} ${specialtyConfig.color}`}>
                              {patientName.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 dark:text-white truncate">
                              {patientName}
                            </p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              {isToday(aptDate) 
                                ? (lang === 'ar' ? 'اليوم' : 'Today')
                                : isTomorrow(aptDate)
                                ? (lang === 'ar' ? 'غداً' : 'Tomorrow')
                                : format(aptDate, 'EEE, MMM d', { locale })}
                              {' • '}
                              {format(aptDate, 'h:mm a', { locale })}
                            </p>
                          </div>
                          {getStatusBadge(apt.appointment_status_id)}
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Alerts & Notifications */}
          <Card className="border-0 shadow-md" data-testid="alerts-section">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  {lang === 'ar' ? 'التنبيهات' : 'Clinical Alerts'}
                </CardTitle>
              </div>
              {alerts.length > 0 && (
                <Badge variant="destructive">{alerts.length}</Badge>
              )}
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                {alerts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                    <CheckCircle2 className="h-12 w-12 mb-4 opacity-50 text-green-500" />
                    <p>{lang === 'ar' ? 'لا توجد تنبيهات' : 'No alerts'}</p>
                    <p className="text-sm mt-1">{lang === 'ar' ? 'كل شيء على ما يرام!' : 'Everything looks good!'}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {alerts.map((alert, index) => (
                      <div 
                        key={index}
                        className={`p-4 rounded-lg border-l-4 ${
                          alert.type === 'danger' 
                            ? 'bg-red-50 border-red-500 dark:bg-red-900/20' 
                            : alert.type === 'warning'
                            ? 'bg-amber-50 border-amber-500 dark:bg-amber-900/20'
                            : 'bg-blue-50 border-blue-500 dark:bg-blue-900/20'
                        }`}
                        data-testid={`alert-${index}`}
                      >
                        <div className="flex items-start gap-3">
                          {getAlertIcon(alert.type)}
                          <div className="flex-1">
                            <p className="font-medium text-slate-900 dark:text-white">
                              {alert.title}
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                              {alert.message}
                            </p>
                            {alert.date && (
                              <p className="text-xs text-slate-400 mt-2">
                                {format(new Date(alert.date), 'PPp', { locale })}
                              </p>
                            )}
                            {alert.patient_id && (
                              <Button 
                                variant="link" 
                                size="sm" 
                                className="p-0 h-auto mt-2"
                                onClick={() => navigate(`/clinic/patients/${alert.patient_id}`)}
                              >
                                {lang === 'ar' ? 'عرض المريض' : 'View Patient'} →
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Recent Records - Specialty specific */}
        {recentRecords.length > 0 && (
          <Card className="border-0 shadow-md" data-testid="recent-records">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className={`h-5 w-5 ${specialtyConfig.color}`} />
                {lang === 'ar' ? 'السجلات الأخيرة' : 'Recent Records'}
              </CardTitle>
              <CardDescription>
                {lang === 'ar' ? `سجلات ${SPECIALTY_CONFIG[currentSpecialty]?.label.ar || 'الطب'}` : `${SPECIALTY_CONFIG[currentSpecialty]?.label.en || 'Medical'} Records`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {recentRecords.slice(0, 6).map((record, index) => (
                  <div 
                    key={index}
                    className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${specialtyConfig.bgColor}`}>
                        {React.createElement(specialtyConfig.icon, { className: `h-4 w-4 ${specialtyConfig.color}` })}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 dark:text-white truncate">
                          {record.patient?.first_name || (lang === 'ar' ? 'مريض' : 'Patient')}
                        </p>
                        <p className="text-sm text-slate-500">
                          {record.record_date && format(new Date(record.record_date), 'PP', { locale })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <Card className="border-0 shadow-md" data-testid="quick-actions">
          <CardHeader>
            <CardTitle>{lang === 'ar' ? 'إجراءات سريعة' : 'Quick Actions'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <Button variant="outline" className="h-24 flex-col gap-2" asChild>
                <Link to="/clinic/patients">
                  <Users className="h-6 w-6" />
                  <span className="text-sm">{lang === 'ar' ? 'المرضى' : 'Patients'}</span>
                </Link>
              </Button>
              <Button variant="outline" className="h-24 flex-col gap-2" asChild>
                <Link to="/doctor/appointments">
                  <Calendar className="h-6 w-6" />
                  <span className="text-sm">{lang === 'ar' ? 'المواعيد' : 'Appointments'}</span>
                </Link>
              </Button>
              <Button variant="outline" className="h-24 flex-col gap-2" asChild>
                <Link to="/clinic/staff">
                  <UserCog className="h-6 w-6" />
                  <span className="text-sm">{lang === 'ar' ? 'الموظفين' : 'Staff'}</span>
                </Link>
              </Button>
              <Button variant="outline" className="h-24 flex-col gap-2" asChild>
                <Link to="/clinic/billing">
                  <DollarSign className="h-6 w-6" />
                  <span className="text-sm">{lang === 'ar' ? 'الفواتير' : 'Billing'}</span>
                </Link>
              </Button>
              <Button variant="outline" className="h-24 flex-col gap-2" asChild>
                <Link to="/clinic/reports">
                  <BarChart3 className="h-6 w-6" />
                  <span className="text-sm">{lang === 'ar' ? 'التقارير' : 'Reports'}</span>
                </Link>
              </Button>
              <Button variant="outline" className="h-24 flex-col gap-2" asChild>
                <Link to="/clinic/settings">
                  <Settings className="h-6 w-6" />
                  <span className="text-sm">{lang === 'ar' ? 'الإعدادات' : 'Settings'}</span>
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClinicDashboard;
